import { gql, useLazyQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import type { Account } from '@interfaces/account';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useContext } from 'react';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import { AFFILIATION_STATE_FIELDS } from './components/affiliationStateFields';

const ACCOUNT = (permissions?: {
  canReadCustodianConnection?: boolean;
}) => `#graphql
  id: stringId
  type
  state
  applyForGovFunds
  jurisdiction
  sourceOfFunds
  eligibleForDisabilityTaxCredit
  repCode { code }
  groupEmployer
  groupNumber
  rightOfSurvivorship
  subAccounts { id state }
  respAllBeneficiariesAreInBc
  user {
    id: stringId
    firstName
    lastName
    primaryEmail
    taxIdExists
    physicalAddress { country city province streetName postal unitNumber houseNumber neighborhood }
    maritalStatus
    countryOfTaxResidence
    type
    accessibleBy (input: { excludeRepCodeUsers: $excludeRepCodeUsers }) {
      relation
      organizationUser {
        id
        repCodes { code name }
      }
    }
  }
  scheduledIncomeFundTransfer {
    id
    scheduledDate
    minimumAnnualAmountCents
    annualAmountCents
    state
    subAccount {
      id
      theme { id translatedName { ${TRANSLATED_STRING_FIELDS} } }
    }
    bankAccount {
      id
      name
      bankAccountNumber
    }
    frequency
    amountPayableType
    dateOfBirth
    taxOption
    specialTaxRate
    totalWithdrawalAmountOption
  }
  householdClientGroup { id: stringId name }
  affiliations { ${AFFILIATION_STATE_FIELDS} }
  baseCurrency
  availableCurrencies
  custodianAffiliates{
    allocation
    relation
    type
    contributionAmount
    grantAmount
    user {
      dateOfBirth
      lastName
      firstName
    }
  }
  ${
    permissions?.canReadCustodianConnection
      ? `#graphql
    custodianConnection {
      id
      name
      default
      accountTypeSettings {
        type
        baseCurrency
        availableCurrencies
        isMultiCurrencyEnabled
        affiliateSettings {
          affiliateType
          requiredFields
          optionalFields
          description
        }
      }
   }
  `
      : ''
  }
`;

export const FETCH_USER_ACCOUNT = (
  derivedCustomFields?: string[],
  permissions?: { canReadCustodianConnection?: boolean },
) => gql`
  query fetchUserAccount($userId: SafeString!, $excludeRepCodeUsers: Boolean) {
    fetchUser(input: { userId: $userId }) {
      user {
        accounts {
          ${ACCOUNT(permissions)}
          ${
            derivedCustomFields
              ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
            key
            value
            customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format }
            selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
          }`
              : ''
          }
        }
      }
    }
  }
`;

export const FETCH_ACCOUNT = (
  derivedCustomFields?: string[],
  permissions?: { canReadCustodianConnection?: boolean },
) => gql`
  query fetchAccount($accountId: SafeString!, $excludeRepCodeUsers: Boolean) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        ${ACCOUNT(permissions)}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

type FetchAccountResult = {
  fetchAccount?: {
    account?: Partial<Account>;
  };
};

export const useFetchAccount = (accountId?: string) => {
  const canReadCustodianConnection = usePermission('read:custodian_connection');
  const { workflowData } = useContext(WorkflowContext);
  const repcodeAccessEnabled = useFeatureFlag(FlagsmithFlags.repcode_access);
  return useLazyQuery<FetchAccountResult>(
    FETCH_ACCOUNT(undefined, { canReadCustodianConnection }),
    {
      variables: {
        accountId: accountId || workflowData?.currentAccountId,
        skipErrorHandler: true,
        excludeRepCodeUsers: !repcodeAccessEnabled,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
  );
};
